import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { ArrowDownRight, ArrowRight, Globe, Palette, Users, ShoppingCart, FileText, Code, Bot } from 'lucide-react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { services } from '../../data/services'

gsap.registerPlugin(ScrollTrigger)

const serviceIcons: Record<string, React.ElementType> = {
  'web-dev': Globe,
  'branding': Palette,
  'crm': Users,
  'ecommerce': ShoppingCart,
  'landing': FileText,
  'saas': Code,
  'ai': Bot,
}

export default function ServicesAccordion() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      const items = sectionRef.current?.querySelectorAll('.service-item')
      if (items) {
        gsap.fromTo(items, { x: -50, opacity: 0 }, {
          x: 0, opacity: 1, duration: 0.6, stagger: 0.1, ease: 'power3.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top 75%', toggleActions: 'play none none none' }
        })
      }
    }, sectionRef)
    return () => ctx.revert()
  }, [])

  return (
    <section ref={sectionRef} className="bg-[#050505] py-16 lg:py-24 px-6 lg:px-10">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-12">
        <div>
          <span className="text-mono text-[#9A9A9A] mb-2 block">WHAT WE DO</span>
          <h2 className="text-h2 uppercase text-white">OUR SERVICES</h2>
        </div>
        <p className="text-[#9A9A9A] max-w-md">
          Seven core services designed to take your business from idea to launch — and beyond.
        </p>
      </div>

      <div className="space-y-0">
        {services.map((service, index) => {
          const Icon = serviceIcons[service.id] || Globe
          return (
            <div key={service.id} className="service-item">
              <div className="-mx-6 lg:-mx-10 overflow-hidden">
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="group relative w-full px-6 lg:px-10 flex items-center justify-between py-6 lg:py-7 border-t border-white/10 cursor-pointer text-left"
              >
                <span className="absolute inset-0 bg-gradient-to-t from-[#B7000E] via-[#b7000f94] to-[#b7000f5f] translate-y-full group-hover:translate-y-0 transition-transform duration-1000 ease-out" />
                <div className="flex items-center gap-4 lg:gap-6 relative z-10">
                  <div className="w-10 h-10 rounded-full bg-[#B7000E] flex items-center justify-center group-hover:bg-white/10 transition-colors">
                  <Icon className="w-4 h-4 text-white group-hover:text-white transition-colors" />
                  </div>
                  <span className="text-xl lg:text-2xl text-white font-light group-hover:text-white transition-colors">
                    {service.title}
                  </span>
                </div>
                <div className="flex items-center gap-4 relative z-10">
                  <ArrowDownRight className={`w-5 h-5 text-[#9A9A9A] transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`} />
                  <span className="text-mono text-[#9A9A9A] hidden sm:block">{service.number}</span>
                </div>
              </button>
              </div>

              <div className={`overflow-hidden transition-all duration-500 ease-out ${openIndex === index ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="pb-8 pl-14 lg:pl-16">
                  <p className="text-[#9A9A9A] max-w-3xl text-base leading-relaxed mb-6">{service.description}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                    {service.features.map((feature) => (
                      <div key={feature} className="flex items-center gap-2">
                        <span className="w-1 h-1 rounded-full bg-white/40" />
                        <span className="text-sm text-white/70">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {service.technologies.map((tech) => (
                      <span key={tech} className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs text-[#9A9A9A]">{tech}</span>
                    ))}
                  </div>
                  <Link to={`/services/${service.id}`} className="inline-flex items-center gap-2 text-sm text-white hover:text-[#9A9A9A] transition-colors">
                    Learn more about {service.title.toLowerCase()} <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}
